/* ═══════════════════════════════════════════════════════════════
   Metals ticker — bounded random walk.

   A port of the no-key fallback in src/data/silver.ts: each metal
   drifts by a small shock, is pulled gently back toward its seed,
   and is clamped to ±2.2% of it. That keeps the numbers plausible
   over a long session instead of wandering off.

   The scroll itself is pure CSS (.gmd-ticker-track), so it keeps
   running even if this script never loads — the server-rendered
   seed prices are already on the page.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var BAND = 0.022; // how far from seed a price may stray
  var PULL = 0.06; // mean-reversion strength per tick
  var SPARK_POINTS = 24;
  var SPARK_W = 46;
  var SPARK_H = 14;

  var money = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  function nextTick(seed, current, drift) {
    var shock = (Math.random() - 0.5) * drift * 2;
    var pull = (seed - current) * PULL;
    var next = current + shock + pull;
    return Math.min(seed * (1 + BAND), Math.max(seed * (1 - BAND), next));
  }

  function sparkPoints(history) {
    if (history.length < 2) return '';
    var min = Math.min.apply(null, history);
    var max = Math.max.apply(null, history);
    var span = max - min || 1;
    return history
      .map(function (v, i) {
        var x = (i / (history.length - 1)) * SPARK_W;
        var y = SPARK_H - ((v - min) / span) * SPARK_H;
        return x.toFixed(2) + ',' + y.toFixed(2);
      })
      .join(' ');
  }

  function Ticker(root) {
    var seedNode = root.querySelector('[data-gmd-ticker-seeds]');
    if (!seedNode) return;

    var metals;
    try {
      metals = JSON.parse(seedNode.textContent).metals;
    } catch (e) {
      return; // a malformed setting must not take the header down
    }
    if (!metals || !metals.length) return;

    // Both duplicated passes carry the same quote at the same index, so a
    // single write per index keeps them in lockstep — which is what makes
    // the seam invisible when the track wraps.
    var quotes = metals.map(function (m, i) {
      return {
        label: String(m.label || ''),
        seed: parseFloat(m.price) || 0,
        drift: parseFloat(m.drift) || 0,
        price: parseFloat(m.price) || 0,
        history: [],
        nodes: Array.prototype.slice.call(root.querySelectorAll('[data-gmd-quote][data-key="' + i + '"]')),
      };
    });

    function paint(q) {
      var pct = q.seed ? ((q.price - q.seed) / q.seed) * 100 : 0;

      // The home page prices grillz off the silver spot, and the ticker is
      // the only thing on the page that knows it. Broadcasting rather than
      // exporting keeps the two sections independent: if the client removes
      // the ticker, the home page keeps its server-rendered seed price.
      document.dispatchEvent(
        new CustomEvent('gmd:spot', { detail: { metal: q.label, price: q.price, changePct: pct } })
      );

      var dir = Math.abs(pct) < 0.005 ? 'flat' : pct > 0 ? 'up' : 'down';
      var arrow = dir === 'down' ? '▾' : '▴';
      var points = sparkPoints(q.history);

      q.nodes.forEach(function (node) {
        var priceEl = node.querySelector('[data-gmd-price]');
        var deltaEl = node.querySelector('[data-gmd-delta]');
        var sparkEl = node.querySelector('[data-gmd-spark] polyline');

        if (priceEl) priceEl.textContent = '$' + money.format(q.price);
        if (deltaEl) {
          deltaEl.textContent = arrow + ' ' + Math.abs(pct).toFixed(2) + '%';
          deltaEl.setAttribute('data-dir', dir);
        }
        if (sparkEl && points) {
          sparkEl.setAttribute('points', points);
          sparkEl.setAttribute(
            'stroke',
            q.history[q.history.length - 1] >= q.history[0] ? 'var(--c-up)' : 'var(--c-down)'
          );
        }
      });
    }

    function tick() {
      quotes.forEach(function (q) {
        q.price = nextTick(q.seed, q.price, q.drift);
        q.history.push(q.price);
        if (q.history.length > SPARK_POINTS) q.history.shift();
        paint(q);
      });
    }

    // Seed the sparkline with a plausible run of history so it is a line on
    // first paint rather than a dot that fills in over two minutes.
    quotes.forEach(function (q) {
      var walk = q.seed;
      for (var i = 0; i < SPARK_POINTS; i++) {
        walk = nextTick(q.seed, walk, q.drift);
        q.history.push(walk);
      }
      q.price = walk;
      paint(q);
    });

    var seconds = parseFloat(root.getAttribute('data-interval')) || 4;
    var timer = setInterval(tick, seconds * 1000);

    // The theme editor tears sections down and rebuilds them; without this
    // every re-render would leave another interval writing to dead nodes.
    root.addEventListener('gmd:teardown', function () {
      clearInterval(timer);
    });
    root.gmdTeardown = function () {
      clearInterval(timer);
    };
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-gmd-ticker]').forEach(function (root) {
      if (root.dataset.gmdReady) return;
      root.dataset.gmdReady = '1';
      Ticker(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-gmd-ticker]').forEach(function (root) {
      if (root.gmdTeardown) root.gmdTeardown();
    });
  });
})();
